// src\themes\lino\components\GridItems.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { loadThemeComponent } from '@/core/config/theme-component-loader';
import { textGroupVariants, textItemVariants } from './animations/textReveal';
const CustomButton = loadThemeComponent('CustomButton');

interface GridItem {
    title: string;
    description: string;
    link?: string | null;
    priority: number;
    buttonContent?: string | null;
    image: {
        id: number;
        url: string;
        description?: string | null;
        name: string;
    } | string;
}

interface GridItemsProps {
    data: {
        items: GridItem[];
    };
}

export default function GridItems({ data }: GridItemsProps) {
    return (
        <div className="col-span-12 relative grid grid-cols-1 gap-12 lg:gap-16">
            {data?.items?.map((item, index) => {
                const isReversed = index % 2 === 1;
                const imageInfo =
                    typeof item.image === 'string' ? { url: '', name: '' } : item.image;

                return (
                    <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center"
                    >
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: false, amount: 0.4 }}
                            variants={textGroupVariants}
                            className={`${isReversed ? 'md:order-2' : ''} space-y-5`}
                        >
                            <motion.span variants={textItemVariants} className="block w-8 h-px bg-primary-900" />
                            {item.title && (
                                <motion.h2 variants={textItemVariants} className="text-2xl lg:text-4xl font-light text-primary-900 leading-[1.15] tracking-tight">
                                    {item.title}
                                </motion.h2>
                            )}
                            {item.description && (
                                <motion.p variants={textItemVariants} className="text-[13px] text-primary-600 leading-7 whitespace-pre-line max-w-md">
                                    {item.description}
                                </motion.p>
                            )}
                            {item.buttonContent && item.link && (
                                <motion.div variants={textItemVariants} className="w-fit">
                                    <CustomButton asChild variant="outline" size="default" className="mt-3">
                                        <Link href={item.link}>{item.buttonContent}</Link>
                                    </CustomButton>
                                </motion.div>
                            )}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.97 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                            className={`${isReversed ? 'md:order-1' : ''} relative aspect-[4/3] bg-primary-100 overflow-hidden rounded-2xl`}
                        >
                            {imageInfo.url && (
                                <Image
                                    src={imageInfo.url}
                                    alt={item.title || imageInfo.name}
                                    fill
                                    className="object-cover"
                                />
                            )}
                        </motion.div>
                    </div>
                );
            })}
        </div>
    );
}